import { forEach, orderBy } from 'lodash';

import CoreUtils from '../../common/utils/CoreUtils';
import StringUtils from '../../common/utils/StringUtils';

import ScheduleDataModel, { hlhCode, llhCode } from '../../schedules/ScheduleDataModel';

import MetadataHelper from '../../common/value-types/MetadataHelper';
import BasicPropertyHelper from '../../schedules/property-helpers/BasicPropertyHelper';
import OfferSelectPackageRuleRenderer from './OfferSelectPackageRuleRenderer';
import EnergyValue from '../../common/value-types/EnergyValue';

const contractDisplayNameField = 'contractDisplayName';

const lightLoadPrice = 'lightLoadPrice';
const heavyLoadPrice = 'heavyLoadPrice';
const isLightLoadElected = 'isLightLoadElected';
const isHeavyLoadElected = 'isHeavyLoadElected';

const hlhEntryField = 'hlhEntryField';
const llhEntryField = 'llhEntryField';
const atcEntryField = 'atcEntryField';

export default class OfferSelectDataModel extends ScheduleDataModel {
    constructor(locale, counterparty, commandDataModel, summaryDataModel, priceDefaultValue) {
        super(locale, counterparty, commandDataModel)

        this.summaryDataModel = summaryDataModel;

        this.priceDefaultValue = StringUtils.isNonEmptyString(priceDefaultValue)
            ? parseFloat(priceDefaultValue)
            : null;
    }

    getDefaultSortCriteria() {
        return {
            fields: ['localDateMs', 'contractCode'],
            order: ['asc', 'asc']
        };
    }

    processFullServerUpdate(scheduleUpdateDto) {
        this.allowToggleElection = scheduleUpdateDto.allowToggleElection;
        this.allowAdjustPrice = scheduleUpdateDto.allowAdjustPrice;

        super.processFullServerUpdate(scheduleUpdateDto);
    }

    processContracts(scheduleUpdateDto) {
        forEach(scheduleUpdateDto.contracts, contractDto => {
            var contract = {};
            Object.assign(contract, contractDto);

            this.registerContract(contract);
        });
    }

    updateScheduleUsage(schedule, updatedDataFields) {
        super.updateScheduleUsage(schedule, updatedDataFields);

        var totalMWh = 0;
        var totalH = 0;

        forEach(
            schedule.hourlyDataFields, dataField => {
                totalMWh += schedule[dataField].averageValue;
                totalH += schedule[dataField].averageDuration;
            }
        );

        if (totalMWh !== schedule.totalMWh) {
            schedule.totalMWh = totalMWh;
            updatedDataFields.push('totalMWh');
        }

        if (totalH !== schedule.totalH) {
            schedule.totalH = totalH;
            updatedDataFields.push('totalH');
        }
    }

    updateOverallStatus() {
        super.updateOverallStatus();

        CoreUtils.iterateProperties(this.indexedContracts, (_, contract) => {
        });

        CoreUtils.iterateProperties(this.indexedSchedules, (_, schedule) => {
            var contract = this.indexedContracts[schedule.contractCode];
            if (contract === undefined)
                return;
        });

        if (CoreUtils.isDefinedAndNotNull(this.summaryDataModel)) {
            var contracts = [];
            CoreUtils.iterateProperties(this.indexedContracts, (_, contract) => {
                contracts.push(contract);
            });

            this.summaryDataModel.updateProperties({
                contracts: orderBy(contracts, ['code'], ['asc']),
                isEmailCheckboxVisible: this.allowToggleElection
            });
        }

        this.hasValidationErrors = false;

        if (CoreUtils.isDefinedAndNotNull(this.commandDataModel)) {
            this.commandDataModel.updateProperties({
                hasValidationErrors: this.hasValidationErrors
            });
        }
    }

    canEditFieldValue(schedule, dataField) {
        if (dataField === isLightLoadElected || dataField === isHeavyLoadElected)
            return this.allowToggleElection && schedule.enableEditValues;

        if (dataField === lightLoadPrice || dataField === heavyLoadPrice)
            return schedule.enableEditValues;

        if (!super.canEditFieldValue(schedule, dataField))
            return false;

        if (dataField === hlhEntryField || dataField === llhEntryField)
            return schedule.enableEditValues

        return false;
    }

    isElectableHourField(schedule, dataField) {
        if (dataField === isLightLoadElected || dataField === isHeavyLoadElected)
            return this.allowToggleElection && schedule.enableEditValues;

        if (dataField === lightLoadPrice || dataField === heavyLoadPrice)
            return schedule.enableEditValues;

        return super.isElectableHourField(schedule, dataField);
    }

    onFieldValuesEditing(schedule, dataFieldValues) {

        var hlhValue, llhValue; 

        if (CoreUtils.getPropertyCount(dataFieldValues) === 1) {
            hlhValue = dataFieldValues[hlhEntryField];
            llhValue = dataFieldValues[llhEntryField];
        }

        if (!super.onFieldValuesEditing(schedule, dataFieldValues))
            return false;

        var contract = this.indexedContracts[schedule.contractCode];
       
        if (CoreUtils.isDefinedAndNotNull(hlhValue))
            dataFieldValues[hlhEntryField] = this.applyMinimumAndMaximum(hlhValue, contract);

        if (CoreUtils.isDefinedAndNotNull(llhValue))
            dataFieldValues[llhEntryField] = this.applyMinimumAndMaximum(llhValue, contract);

        return this.filterUpdatingElectableHourFields(
            schedule,
            dataFieldValues,
            this.indexedContracts[schedule.contractCode]
        );
    }

    onScheduleCreated(schedule) {
        super.onScheduleCreated(schedule);
    }

    onScheduleUpdated(schedule, isServerUpdate, isPeerUpdate, updatedDataFields, quickEntryHints) {
      super.onScheduleUpdated(schedule, isServerUpdate, isPeerUpdate, updatedDataFields, quickEntryHints);

        if (!isServerUpdate && !isPeerUpdate) {
            this.enforceSchedulePackageValue(schedule, updatedDataFields, quickEntryHints);
        }
    }

    onSchedulePropertiesCreating(schedule, contract, scheduleDto) {
        super.onSchedulePropertiesCreating(schedule, contract, scheduleDto);

        BasicPropertyHelper.onPropertyCreating(
            schedule,
            isLightLoadElected,
            llhCode,
            scheduleDto.metaValues[isLightLoadElected],
            scheduleDto.firstUtcElectionCutoff
        );

        BasicPropertyHelper.onPropertyCreating(
            schedule,
            lightLoadPrice,
            llhCode,
            scheduleDto.metaValues[lightLoadPrice],
            scheduleDto.firstUtcElectionCutoff
        );

        BasicPropertyHelper.onPropertyCreating(
            schedule,
            isHeavyLoadElected,
            hlhCode,
            scheduleDto.metaValues[isHeavyLoadElected],
            scheduleDto.firstUtcElectionCutoff
        );

        BasicPropertyHelper.onPropertyCreating(
            schedule,
            heavyLoadPrice,
            hlhCode,
            scheduleDto.metaValues[heavyLoadPrice],
            scheduleDto.firstUtcElectionCutoff
        );
    }

    onSchedulePropertiesServerUpdate(schedule, contract, scheduleDto, isPeerUpdate, updatedDataFields) {
        super.onSchedulePropertiesServerUpdate(schedule, contract, scheduleDto, isPeerUpdate, updatedDataFields);

        BasicPropertyHelper.onPropertyServerUpdate(
            schedule,
            isLightLoadElected,
            scheduleDto.metaValues[isLightLoadElected],
            true,
            isPeerUpdate,
            updatedDataFields,
            this.locale
        );

        BasicPropertyHelper.onPropertyServerUpdate(
            schedule,
            lightLoadPrice,
            scheduleDto.metaValues[lightLoadPrice],
            true,
            isPeerUpdate,
            updatedDataFields,
            this.locale
        );

        BasicPropertyHelper.onPropertyServerUpdate(
            schedule,
            isHeavyLoadElected,
            scheduleDto.metaValues[isHeavyLoadElected],
            true,
            isPeerUpdate,
            updatedDataFields,
            this.locale
        );

        BasicPropertyHelper.onPropertyServerUpdate(
            schedule,
            heavyLoadPrice,
            scheduleDto.metaValues[heavyLoadPrice],
            true,
            isPeerUpdate,
            updatedDataFields,
            this.locale
        );
    }

    onSchedulePropertiesModifying(schedule, contract, scheduleUpdateSet, updatedDataFields) {
        super.onSchedulePropertiesModifying(schedule, contract, scheduleUpdateSet, updatedDataFields);

        BasicPropertyHelper.onPropertyModifying(
            schedule,
            isLightLoadElected,
            schedule[isLightLoadElected],
            scheduleUpdateSet,
            updatedDataFields,
            this.locale
        );

        BasicPropertyHelper.onPropertyModifying(
            schedule,
            lightLoadPrice,
            schedule[lightLoadPrice],
            scheduleUpdateSet,
            updatedDataFields,
            this.locale
        );

        BasicPropertyHelper.onPropertyModifying(
            schedule,
            isHeavyLoadElected,
            schedule[isHeavyLoadElected],
            scheduleUpdateSet,
            updatedDataFields,
            this.locale
        );

        BasicPropertyHelper.onPropertyModifying(
            schedule,
            heavyLoadPrice,
            schedule[heavyLoadPrice],
            scheduleUpdateSet,
            updatedDataFields,
            this.locale
        );
    }

    onSchedulePropertiesEditing(schedule, dataFieldHints, updatedDataFields) {
        super.onSchedulePropertiesEditing(schedule, dataFieldHints, updatedDataFields);

        BasicPropertyHelper.onPropertyEditing(
            schedule,
            isLightLoadElected,
            dataFieldHints,
            updatedDataFields
        );

        BasicPropertyHelper.onPropertyEditing(
            schedule,
            lightLoadPrice,
            dataFieldHints,
            updatedDataFields
        );

        BasicPropertyHelper.onPropertyEditing(
            schedule,
            isHeavyLoadElected,
            dataFieldHints,
            updatedDataFields
        );

        BasicPropertyHelper.onPropertyEditing(
            schedule,
            heavyLoadPrice,
            dataFieldHints,
            updatedDataFields
        );
    }

    onSchedulePropertiesCutoffCheck(schedule, serverUtcTimeMs, warningPeriodMs, updatedDataFields) {
        super.onSchedulePropertiesCutoffCheck(schedule, serverUtcTimeMs, warningPeriodMs, updatedDataFields);

        BasicPropertyHelper.onPropertyCutoffCheck(
            schedule,
            isLightLoadElected,
            updatedDataFields,
            serverUtcTimeMs,
            warningPeriodMs
        );

        BasicPropertyHelper.onPropertyCutoffCheck(
            schedule,
            lightLoadPrice,
            updatedDataFields,
            serverUtcTimeMs,
            warningPeriodMs
        );

        BasicPropertyHelper.onPropertyCutoffCheck(
            schedule,
            isHeavyLoadElected,
            updatedDataFields,
            serverUtcTimeMs,
            warningPeriodMs
        );

        BasicPropertyHelper.onPropertyCutoffCheck(
            schedule,
            heavyLoadPrice,
            updatedDataFields,
            serverUtcTimeMs,
            warningPeriodMs
        );
    }

    onSchedulePropertiesReverting(schedule, updatedDataFields) {
        super.onSchedulePropertiesReverting(schedule, updatedDataFields);

        BasicPropertyHelper.onPropertyReverting(
            schedule,
            isLightLoadElected,
            updatedDataFields
        );

        BasicPropertyHelper.onPropertyReverting(
            schedule,
            lightLoadPrice,
            updatedDataFields
        );

        BasicPropertyHelper.onPropertyReverting(
            schedule,
            isHeavyLoadElected,
            updatedDataFields
        );

        BasicPropertyHelper.onPropertyReverting(
            schedule,
            heavyLoadPrice,
            updatedDataFields
        );

        schedule[hlhEntryField] = new EnergyValue(0);
        schedule[llhEntryField] = new EnergyValue(0);

        this.revertSchedulePackageQuickEntryFields(schedule);
    }

    onSchedulePropertiesSerialize(schedule, scheduleSubmissionDto) {
        super.onSchedulePropertiesSerialize(schedule, scheduleSubmissionDto);

        scheduleSubmissionDto.metaValues[isLightLoadElected] = schedule[isLightLoadElected];
        scheduleSubmissionDto.metaValues[lightLoadPrice] = schedule[lightLoadPrice];
        scheduleSubmissionDto.metaValues[heavyLoadPrice] = schedule[heavyLoadPrice];
        scheduleSubmissionDto.metaValues[isHeavyLoadElected] = schedule[isHeavyLoadElected];
    }

    onSchedulesInitialized(schedules) {
        super.onSchedulesInitialized(schedules);

        schedules.forEach(
            schedule => this.determinePackageRuleFlags(
                schedule,
                contractDisplayNameField,
                (packageRule, otherDates) => OfferSelectPackageRuleRenderer(
                    new Date(schedule.scheduleDate),
                    packageRule.canConfigureIndependently,
                    otherDates,
                    'elected',
                    this.locale
                )
            )
        );
    }

    isHourModifiable(schedule, dataField, hourTypeCode) {
        var metadataField = MetadataHelper.resolveMetadataField(dataField);

        return (CoreUtils.isUndefinedOrNull(hourTypeCode) || schedule[metadataField].hourTypeCode === hourTypeCode)
            && schedule[metadataField].isElectableHour
            && schedule.enableEditValues
            && (schedule.enablePostCutoffChanges || schedule[metadataField].electionCutoffs.some(electionCutoff => !electionCutoff.isPastElectionCutoff));
    }

    enforceSchedulePackageValue(schedule, updatedDataFields, quickEntryHints) {
        var dateKey = ScheduleDataModel.generateDateKey(schedule.scheduleDate);
        var packageRule = this.indexedPackageRules[schedule.contractCode][dateKey];

        if (CoreUtils.isUndefinedOrNull(packageRule))
            return;

        if (packageRule.canConfigureIndependently)
            return;


        var packageDataFields = [];
        updatedDataFields.forEach(updatedDataField => {
            if (schedule.hourlyDataFields.indexOf(updatedDataField) !== -1
                || updatedDataField === isLightLoadElected
                || updatedDataField === lightLoadPrice
                || updatedDataField === isHeavyLoadElected
                || updatedDataField === heavyLoadPrice)
                packageDataFields.push(updatedDataField);
        })

        if (packageDataFields.length === 0)
            return;

        var scheduleUpdateSets = [];
        forEach(packageRule.packageDateKeys, packageDateKey => {
            var scheduleDateKey = ScheduleDataModel.generateScheduleDateKey(
                packageDateKey,
                schedule.contractCode
            );

            var packageSchedule = this.dateIndexedSchedules[scheduleDateKey];
           
            if (CoreUtils.isDefinedAndNotNull(packageSchedule) && packageSchedule.scheduleId !== schedule.scheduleId) {
                var values = {};

                if (quickEntryHints) {
                    if (CoreUtils.isDefinedAndNotNull(quickEntryHints[hlhEntryField])) {
                        packageSchedule.hourlyDataFields.forEach(field => {
                            let metadataField = MetadataHelper.resolveMetadataField(field);
                            if (!packageDataFields.includes(field) && packageSchedule[metadataField].hourTypeCode === hlhCode) {
                                values[field] = quickEntryHints[hlhEntryField];
                            }
                        });
                    } else if (CoreUtils.isDefinedAndNotNull(quickEntryHints[llhEntryField])) {
                        packageSchedule.hourlyDataFields.forEach(field => {
                            let metadataField = MetadataHelper.resolveMetadataField(field);
                            if (!packageDataFields.includes(field) && packageSchedule[metadataField].hourTypeCode === llhCode) {
                                values[field] = quickEntryHints[llhEntryField];
                            }
                        });
                    } else if (CoreUtils.isDefinedAndNotNull(quickEntryHints[atcEntryField])) {
                        packageSchedule.hourlyDataFields.forEach(field => {
                            if (!packageDataFields.includes(field)) {
                                values[field] = quickEntryHints[atcEntryField];
                            }
                        });
                    }
                }
                
                packageDataFields.forEach(packageDataField => {
                    let metadataField = MetadataHelper.resolveMetadataField(packageDataField);
                    if (CoreUtils.isDefinedAndNotNull(packageSchedule[metadataField]) && schedule[metadataField].hourTypeCode == packageSchedule[metadataField].hourTypeCode) {
                        values[packageDataField] = schedule[packageDataField];
                    }
                });

                if (CoreUtils.getPropertyCount(values) > 0) {
                    if (quickEntryHints) {
                        if (CoreUtils.isDefinedAndNotNull(quickEntryHints[hlhEntryField])) {
                            packageSchedule.hlhEntryField = quickEntryHints[hlhEntryField];
                        } else if (CoreUtils.isDefinedAndNotNull(quickEntryHints[llhEntryField])) {
                            packageSchedule.llhEntryField = quickEntryHints[llhEntryField];
                        }
                    }

                    scheduleUpdateSets.push({
                        scheduleId: packageSchedule.scheduleId,
                        values: values
                    });
                }
            }
        });

        if (scheduleUpdateSets.length > 0)
            this.updateSchedules(scheduleUpdateSets, false, true);
    }

    revertSchedulePackageQuickEntryFields(schedule) {
        var dateKey = ScheduleDataModel.generateDateKey(schedule.scheduleDate);
        var packageRule = this.indexedPackageRules[schedule.contractCode][dateKey];

        if (CoreUtils.isUndefinedOrNull(packageRule))
            return;

        if (packageRule.canConfigureIndependently)
            return;

        forEach(packageRule.packageDateKeys, packageDateKey => {
            var scheduleDateKey = ScheduleDataModel.generateScheduleDateKey(
                packageDateKey,
                schedule.contractCode
            );

            var packageSchedule = this.dateIndexedSchedules[scheduleDateKey];

            if (CoreUtils.isDefinedAndNotNull(packageSchedule) && packageSchedule.scheduleId !== schedule.scheduleId) {
                packageSchedule[hlhEntryField] = schedule[hlhEntryField];
                packageSchedule[llhEntryField] = schedule[llhEntryField];
            }
        });
    }

    applyMinimumAndMaximum(quickEntryField, contract) {
        var hourlyEnergyMinimum = new EnergyValue(contract.hourlyEnergyMinimum);
        var hourlyEnergyMaximum = new EnergyValue(contract.hourlyEnergyMaximum);

        quickEntryField = quickEntryField.applyMinimum(
            hourlyEnergyMinimum,
            contract.subhourlyResolution
        );

        quickEntryField = quickEntryField.applyMaximum(
            hourlyEnergyMaximum,
            contract.subhourlyResolution
        );

        return quickEntryField;
    }
}